import React, { useState, useEffect, useRef, useCallback } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { usePost } from '../../hooks/usePost';
import LinkPreview from '../Components/LinkPreview/LinkPreview';
import "./AddPost.scss";
import { convertToMarkdown, convertMarkdownToHtml, getMentionConfig } from '../helpers/helper';
import { extractFirstLink } from '../helpers/helper';
import { useSelector } from 'react-redux';
import { FaPaperclip, FaPlus, FaLink } from 'react-icons/fa';
import GiphySearchBox from 'react-giphy-searchbox';
import {fetchUsers} from "../../utils/userSuggest";
import { FaAt } from "react-icons/fa";
import { createRoot } from "react-dom/client";

// Custom Link Modal Component
const LinkModal = ({ isOpen, onClose, onSave, initialUrl = "", initialText = "", isEdit = false }) => {
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState(initialText);
  const [error, setError] = useState("");
  const urlInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl);
      setText(initialText);
      setError("");
      setTimeout(() => {
        if (urlInputRef.current) urlInputRef.current.focus();
      }, 100);
    }
  }, [isOpen, initialUrl, initialText]);

  const validateUrl = (url) => {
    if (!url.trim()) return false;
    // Stricter URL validation: must start with http/https, valid domain, no spaces
    const urlPattern = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
    return urlPattern.test(url.trim());
  };

  const handleSave = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const trimmedUrl = url.trim();
    const trimmedText = text.trim();
    if (!trimmedUrl) {
      setError("URL is required");
      return;
    }
    if (!validateUrl(trimmedUrl)) {
      setError("Please enter a valid URL");
      return;
    }
    let finalUrl = trimmedUrl;
    if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      finalUrl = 'https://' + trimmedUrl;
    }
    onSave({ url: finalUrl, text: trimmedText || finalUrl });
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleRemoveLink = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    onSave({ url: null, text: text });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="link-modal-overlay">
      <div className="link-modal" onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
        <div className="link-modal-header">
          <h3>{isEdit ? 'Edit Link' : 'Add Link'}</h3>
          <button className="close-btn" onClick={e => { e.preventDefault(); e.stopPropagation(); onClose(); }} aria-label="Close">×</button>
        </div>
        <div className="link-modal-body">
          <div className="form-group">
            <label htmlFor="link-url">URL</label>
            <input
              ref={urlInputRef}
              id="link-url"
              type="text"
              value={url}
              onChange={e => { setUrl(e.target.value); setError(""); }}
              onKeyDown={handleKeyDown}
              placeholder="https://example.com"
              className={error ? 'error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="link-text">Display Text (optional)</label>
            <input
              id="link-text"
              type="text"
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Link text"
            />
            <small className="form-help">If empty, the URL will be used as display text</small>
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="link-modal-footer">
          {isEdit && (
            <button type="button" className="remove-link-btn" onClick={handleRemoveLink}>Remove Link</button>
          )}
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={e => { e.preventDefault(); e.stopPropagation(); onClose(); }}>Cancel</button>
            <button type="button" className="save-btn" onClick={handleSave}>{isEdit ? 'Update' : 'Add'} Link</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddPost = ({ onClose, isEditing = false, editingPost = null, onEditSave = null}) => {
  // Track count of existing poll options if editing
  const selectedCategoryId = useSelector((state) => state.categories.selectedCategory);
  const [existingPollOptionCount, setExistingPollOptionCount] = useState(0);
  const [postType, setPostType] = useState('POST');
  const [content, setContent] = useState('');
  // Store media files as objects with file and url
  const [mediaFiles, setMediaFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(selectedCategoryId || '');
  const [url, setUrl] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [pollData, setPollData] = useState({
    question: '',
    options: ['', ''],
  });
  const [validationError, setValidationError] = useState("");
  const { createNewPost, handleImageUploads } = usePost();
  const quillRef = useRef(null);
  const editorRef = useRef(null);
  const popupContentRef = useRef(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkModalData, setLinkModalData] = useState({
    url: "",
    text: "",
    isEdit: false,
    selection: null
  });

  const categories = useSelector((state) => state.categories.entities);
  const currentUser = useSelector((state) => state.projectInfo.uid);
  // Check if categories exist and have items
  const hasCategoriesAvailable = categories && categories?.length > 0;
  const currentMentionListRef = useRef([]);
  const lastEmptySearchRef = useRef("");

  function selectionHasMention(quill) {
    const range = quill.getSelection();
    if (!range) return false;

    const length = range.length || 1;
    const contents = quill.getContents(range.index, length);

    return contents.ops?.some(
        op => typeof op.insert === "object" && op.insert.mention
    );
  }

  // Initialize state if editing an existing post
  useEffect(() => {
    if (isEditing && editingPost) {
      setContent(editingPost.content || '');
      setSelectedCategory(editingPost.cat || '');
      // Process existing media
      if (editingPost.media && Object.keys(editingPost.media)?.length > 0) {
        // Convert media object to array format for display
        const mediaArray = Object.entries(editingPost.media).map(([key, media]) => ({
          id: key,
          url: media.url,
          type: media.type,
          isExisting: true
        }));
        setExistingMedia(mediaArray);
      }
      // Load GIF if present in editingPost
      if (editingPost.gif_url) {
        setSelectedGif({ url: editingPost.gif_url });
      }
      if (editingPost.type === 'POLL') {
        setPostType('POLL');
        // Support both editingPost.poll and editingPost.data for poll structure
        if (editingPost.poll) {
          const options = Object.values(editingPost.poll.opts).map(opt => opt.text);
          setPollData({
            question: editingPost.poll.ques || '',
            options: options?.length > 0 ? options : ['', '']
          });
          setExistingPollOptionCount(options.length);
        } else if (editingPost.data && editingPost.data.options) {
          // Support for poll data in editingPost.data (API structure)
          const options = editingPost.data.options.map(opt => opt.option_text || '');
          setPollData({
            question: editingPost.data.question || '',
            options: options.length > 0 ? options : ['', '']
          });
          setExistingPollOptionCount(options.length);
        }
      } else {
        setPostType('POST');
        setExistingPollOptionCount(0);
      }
    }
  }, [isEditing, editingPost]);

  // Function to clean up Quill editor
  const cleanUpQuill = () => {
    if (quillRef.current) {
      // Find and remove the toolbar element
      const toolbarElement = document.querySelector('.ql-toolbar');
      if (toolbarElement) {
        toolbarElement.remove();
      }
      // Clear editor content
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
      quillRef.current = null;
      // Reset content when cleaning up
      setContent('');
    }
  };

  // Handle post type switching
  const handlePostTypeChange = (type) => {
    if (type !== postType) {
      cleanUpQuill();
      setPostType(type);
      // Clear any validation errors when changing post type
      setValidationError("");
    }
  };

  useEffect(() => {
    if (editorRef.current) {
      cleanUpQuill(); if (postType === 'POST') {
        const toolbarOptions = [
          ['bold', 'italic', 'underline'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ['link'],
          ['clean'],
        ];

        const quill = new Quill(editorRef.current, {
          formats: ["bold", "italic", "underline", "list", "bullet", "mention", "link"],
          modules: {
            keyboard: {
              bindings: {
                bold: {
                  key: "b",
                  shortKey: true,
                  handler(range) {
                    if (selectionHasMention(this.quill, range)) {
                      // allow formatting but strip from mentions later
                      return true;
                    }
                    this.quill.format("bold", !this.quill.getFormat(range).bold);
                    return false;
                  }
                },
                italic: {
                  key: "i",
                  shortKey: true,
                  handler(range) {
                    if (selectionHasMention(this.quill, range)) {
                      return true;
                    }
                    this.quill.format("italic", !this.quill.getFormat(range).italic);
                    return false;
                  }
                },
                underline: {
                  key: "u",
                  shortKey: true,
                  handler(range) {
                    if (selectionHasMention(this.quill, range)) {
                      return true;
                    }
                    this.quill.format("underline", !this.quill.getFormat(range).underline);
                    return false;
                  }
                }
              }
            },
            toolbar: {
              container: toolbarOptions,
              handlers: {
                link: function () {
                  const quill = this.quill;
                  let selection = quill.getSelection();
                  if (!selection) {
                    quill.focus();
                    return;
                  }
                  // If nothing is selected, check if cursor is inside a link
                  let index = selection.index;
                  let length = selection.length;
                  let [leaf, offset] = quill.getLeaf(index);
                  let format = quill.getFormat(index, length || 1);
                  let selectedText = '';
                  let isEdit = false;
                  let url = '';
                  if (format.link) {
                    // Cursor is inside a link or selection is a link
                    // Expand selection to cover the whole link if needed
                    let ops = quill.getContents().ops;
                    let linkStart = index;
                    let linkEnd = index;
                    // Find the start of the link
                    while (linkStart > 0 && quill.getFormat(linkStart - 1, 1).link === format.link) {
                      linkStart--;
                    }
                    // Find the end of the link
                    while (linkEnd < quill.getLength() && quill.getFormat(linkEnd, 1).link === format.link) {
                      linkEnd++;
                    }
                    selectedText = quill.getText(linkStart, linkEnd - linkStart);
                    url = format.link;
                    isEdit = true;
                    selection = { index: linkStart, length: linkEnd - linkStart };
                  } else {
                    // Not inside a link
                    selectedText = quill.getText(index, length);
                    url = '';
                    isEdit = false;
                  }
                  setLinkModalData({
                    url: url || '',
                    text: selectedText || '',
                    isEdit,
                    selection
                  });
                  setShowLinkModal(true);
                }
              }
            },
            mention: getMentionConfig({ currentMentionListRef, fetchUsers, lastEmptySearchRef }),
          },
          placeholder: "What's on your mind?",
          theme: 'snow'
        });

        // Set the ref immediately after creation
        quillRef.current = quill;

        // 🔒 BLOCK formatting when cursor is on a mention
        const toolbar = quillRef.current.getModule("toolbar");

        const blockedFormats = ["bold", "italic", "underline", "strike"];

        blockedFormats.forEach((format) => {
          toolbar.addHandler(format, () => {
            if (selectionHasMention(quillRef.current)) {
              return; // 🚫 do nothing
            }

            const current = quillRef.current.getFormat()[format];
            quillRef.current.format(format, !current);
          });
        });
        // ... inside your useEffect after initializing Quill:
        if (editorRef.current && quillRef.current) {
          const toolbar = editorRef.current.parentNode.querySelector('.ql-toolbar');
          if (toolbar && !toolbar.querySelector('.ql-mention-btn')) {
            const mentionBtn = document.createElement('button');
            mentionBtn.type = 'button';
            mentionBtn.className = 'ql-mention-btn';
            mentionBtn.title = 'Mention User';

            // Render the GoMention icon
            const iconContainer = document.createElement('span');
            mentionBtn.appendChild(iconContainer);
            createRoot(iconContainer).render(<FaAt  />);

            mentionBtn.onclick = () => {
              const quill = quillRef.current;
              if (quill) {
                quill.getModule('mention').openMenu('@');
                quill.focus();
              }
            };
            // Insert after the underline button
            const underBtn = toolbar.querySelector('.ql-underline');
            if (underBtn) {
              underBtn.parentNode.insertBefore(mentionBtn, underBtn.nextSibling);
            } else {
              toolbar.appendChild(mentionBtn);
            }
          }
        }

        // Add image paste blocking
        // Block 1: Intercept paste at browser level - only block direct image files
        quill.root.addEventListener('paste', (e) => {
          const clipboardData = e.clipboardData || window.clipboardData;
          
          if (!clipboardData) return;
          
          const items = clipboardData.items;
          let hasImageFile = false;
          
          // Check if clipboard contains actual image files (not HTML img tags)
          if (items) {
            for (let i = 0; i < items.length; i++) {
              if (items[i].type.indexOf('image') !== -1 && items[i].kind === 'file') {
                hasImageFile = true;
                break;
              }
            }
          }
          
          if (hasImageFile) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            
            alert('Please use the Photos button below to add images to your post.');
            return false;
          }
          
          // Allow all other content including HTML with img tags (they'll be stripped by Quill's text-change handler)
        }, true); // Use capture phase

        // Block 2: Use Quill's paste event instead of overriding clipboard
        quill.on('paste', function(range, text, html, source) {
          // This runs after Quill processes the paste
          // We'll rely on the text-change handler to catch any images
          // No need to override the clipboard behavior
        });

        // Block 3: Monitor text changes and remove any images that slip through
        const Delta = Quill.import('delta');
        quill.on('text-change', (delta, oldDelta, source) => {
          if (source === 'user') {
            // Check if any images were inserted
            const currentContents = quill.getContents();
            let hasImage = false;
            let imageIndex = -1;
            
            currentContents.ops.forEach((op, index) => {
              if (op.insert && typeof op.insert === 'object' && op.insert.image) {
                hasImage = true;
                imageIndex = index;
              }
            });
            
            if (hasImage) {
              // Remove the image
              const newOps = currentContents.ops.filter(op => {
                return !(op.insert && typeof op.insert === 'object' && op.insert.image);
              });
              
              quill.setContents(new Delta(newOps), 'silent');
              
              alert('Please use the Photos button below to add images to your post.');
            }
          }
          
          // Continue with existing text-change logic
          const text = quill.root.innerHTML;
          setContent(convertToMarkdown(text));
        });

        // If we're editing, set the content in the editor
        if (isEditing && editingPost) {
          quill.root.innerHTML = convertMarkdownToHtml(editingPost?.content || '');
        }
      }
    }

    // Cleanup function when component unmounts
    return () => {
      cleanUpQuill();
    };
  }, [postType, isEditing, editingPost]);

  useEffect(() => {
    setUrl(extractFirstLink(content));
  }, [content]);

  const handleMediaSelect = (event) => {
    const files = Array.from(event.target.files);
    const totalImages = (existingMedia?.length || 0) + (mediaFiles?.length || 0) + files.length;
    if (totalImages > 10) {
      alert("You can upload a maximum of 10 images per post.");
      return;
    }
    const newMedia = files.map(file => ({ file, url: URL.createObjectURL(file) }));
    setMediaFiles(prevFiles => [...prevFiles, ...newMedia]);
  };

  const handlePollOptionChange = (index, value) => {
    setPollData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };
  const addPollOption = () => {
    if (pollData.options?.length < 5) {
      setPollData(prev => ({
        ...prev,
        options: [...prev.options, '']
      }));
    }
  };

  const removePollOption = (index) => {
    if (pollData.options?.length > 2) {
      setPollData(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    }
  };
  const handleLinkSave = ({ url, text }) => {
    const quill = quillRef.current;
    const { selection } = linkModalData;
    if (!quill || !selection) return;
    // Remove link
    if (url === null) {
      if (linkModalData.isEdit) {
        quill.formatText(selection.index, selection.length, 'link', false);
      }
    } else {
      if (linkModalData.isEdit) {
        // Update link URL and/or text
        const currentText = quill.getText(selection.index, selection.length);
        if (currentText !== text) {
          quill.deleteText(selection.index, selection.length);
          quill.insertText(selection.index, text, { link: url });
        } else {
          quill.formatText(selection.index, selection.length, 'link', url);
        }
      } else {
        // Insert new link
        if (selection.length > 0) {
          quill.deleteText(selection.index, selection.length);
          quill.insertText(selection.index, text, { link: url });
        } else {
          quill.insertText(selection.index, text, { link: url });
        }
      }
    }
    quill.setSelection(selection.index + (text ? text.length : 0), 0);
    quill.focus();
  };

  const removeExistingMedia = (mediaId) => {
    setExistingMedia(prevMedia => prevMedia.filter(media => media.id !== mediaId));
  };

  const removeMedia = (index) => {
    setMediaFiles(prevFiles => {
      const fileToRemove = prevFiles[index];
      if (fileToRemove && fileToRemove.url) {
        URL.revokeObjectURL(fileToRemove.url);
      }
      return prevFiles.filter((_, i) => i !== index);
    });
  };
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling

    // Use ref for synchronous check
    if (isSubmittingRef.current) {
      console.log('Already submitting, ignoring duplicate submission');
      return;
    }

    // Set both ref and state
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setValidationError("");

    try {
      const isContentEmpty = (text) => {
        if (!text.trim()) return true;
        const cleanedText = text.replace(/[\s\u200B-\u200D\u2800-\u28FF\uFEFF\u3000]/g, '');
        return cleanedText?.length === 0;
      };

      // Validate content for post
      if (postType === 'POST' && isContentEmpty(content)) {
        setValidationError("Please add some actual content to your post");
        return;
      }

      // Prepare API body
      let apiBody = {
        uid: currentUser,
        ...(selectedCategory !== null && selectedCategory !== "" && { category: Number(selectedCategory) }),
        type: postType,
      };

      if (postType === 'POLL') {
        apiBody.content = pollData.question;
        // For edit, only send new poll options (not the already existing ones)
        if (isEditing && editingPost && editingPost.type === 'POLL') {
          let existingCount = 0;
          if (editingPost.poll && editingPost.poll.opts) {
            existingCount = Object.values(editingPost.poll.opts).length;
          } else if (editingPost.data && editingPost.data.options) {
            existingCount = editingPost.data.options.length;
          }
          const newOptions = pollData.options.slice(existingCount);
          apiBody.poll = {
            question: pollData.question,
            options: newOptions.map((text, idx) => ({ text, key: existingCount + idx + 1 }))
          };
        } else {
          apiBody.poll = {
            question: pollData.question,
            options: pollData.options.map((text, idx) => ({ text, key: idx + 1 }))
          };
        }
      } else {
        apiBody.content = content;
        let allMedia = [];
        const originalMediaCount = editingPost && editingPost.media ? Object.keys(editingPost.media).length : 0;
        const currentMediaCount = (existingMedia?.length || 0) + (mediaFiles?.length || 0);
        let mediaChanged = false;
        // If editing, check if media was added or removed
        if (isEditing) {
          if (currentMediaCount !== originalMediaCount) {
            mediaChanged = true;
          }
        } else {
          // For create, always include media if any
          mediaChanged = true;
        }
        // If media changed or it's a new post, build media array
        if (mediaChanged) {
          // Existing media
          if (existingMedia && existingMedia.length > 0) {
            allMedia = allMedia.concat(existingMedia.map(m => ({ url: m.url, type: m.type })));
          }
          // Upload new media files if any
          if (mediaFiles?.length > 0) {
            try {
              const response = await handleImageUploads(mediaFiles.map(m => m.file));
              const data = response?.data?.data;
              if (data && Array.isArray(data) && data?.length > 0) {
                const imageData = data[0]?.files;
                imageData?.forEach((media) => {
                  allMedia.push({ url: media.path, type: 'image' });
                });
              }
            } catch (error) {
              console.error("Error uploading files:", error);
              setValidationError("Failed to upload media files. Please try again.");
              return;
            }
          }
          // Only set media in API body if there are changes
          apiBody.media = allMedia;
        }
        // GIF
        if (selectedGif) {
          // Try to get dimensions
          let gifObj = { url: selectedGif.url };
          try {
            const img = new window.Image();
            const getDims = () => new Promise((resolve) => {
              img.onload = () => resolve({ width: img.width, height: img.height });
              img.src = selectedGif.url;
            });
            const dims = await getDims();
            gifObj = { ...gifObj, ...dims };
          } catch (e) { }
          apiBody.gif = gifObj;
        }
      }

      // If editing, call handleEditSave
      if (isEditing) {
        // Add media positions if media exists
        if (apiBody.media && Array.isArray(apiBody.media)) {
          apiBody.media = apiBody.media.map((m, idx) => ({ ...m, position: idx + 1 }));
        }
        if (editingPost?.gif_url && !selectedGif) {
          apiBody.gif = {
            url: null,
            width: null,
            height: null
          };
        }

        // For poll, only send new options with correct key
        if (apiBody.poll && Array.isArray(apiBody.poll.options) && editingPost && editingPost.type === 'POLL') {
          // Get count of existing options
          const existingOpts = editingPost.poll && editingPost.poll.opts ? Object.values(editingPost.poll.opts) : [];
          const existingCount = existingOpts.length;
          // Only send new options (those added after existingCount)
          const newOptions = apiBody.poll.options.slice(existingCount);
          apiBody.poll.options = newOptions.map((opt, idx) => ({ ...opt, key: existingCount + idx + 1 }));
        }
        await onEditSave(editingPost.postId, apiBody);

      } else {
        await createNewPost(apiBody);
      }
      onClose();
    } catch (error) {
      console.error(isEditing ? "Error updating post:" : "Error creating post:", error);
      setValidationError(isEditing ? "Failed to update post. Please try again." : (error.message || "Failed to create post. Please try again."));
    } finally {
      // Reset both ref and state
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [mediaFiles, existingMedia, content, postType, pollData, selectedCategory, currentUser, onClose, isEditing, editingPost, onEditSave, handleImageUploads, selectedGif, createNewPost]);

  // Revoke all blob URLs on unmount
  useEffect(() => {
    return () => {
      mediaFiles.forEach(media => {
        if (media.url) URL.revokeObjectURL(media.url);
      });
    };
  }, [mediaFiles]);

  return (
    <div className="upload-popup">
      <div className="upload-popup-content" ref={popupContentRef}>
        {/* <button onClick={onClose} className="close-button-addpost" aria-label="Close">
          ×
        </button> */}

        <div className="create-post-container">
          {/* <h2>{isEditing ? "Edit Post" : "Create New Post"}</h2> */}

          <form onSubmit={handleSubmit}>
            <div className="form-header-row">
              {!isEditing && (
                <div className="post-type-selector">
                  <button
                    className={`type-button ${postType === 'POST' ? 'active' : ''}`}
                    onClick={() => handlePostTypeChange('POST')}
                    type="button"
                  >
                    <FaPlus /> Post
                  </button>
                  <button
                    className={`type-button ${postType === 'POLL' ? 'active' : ''}`}
                    onClick={() => handlePostTypeChange('POLL')}
                    type="button"
                  >
                    <FaPlus /> Poll
                  </button>
                </div>
              )}
              {hasCategoriesAvailable && (
                <select
                  id="category"
                  className="category-dropdown"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  placeholder="Category"
                  value={selectedCategory}
                >
                  <option value="">Select Category</option>
                  {/*
                    Excluded the FWFG announcement category - ID : 22 from the category to avoid post creation
                    under that category, only admins can post there from dashboard
                  */}
                  {categories?.filter(item=>item.id!==22).map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              )}
            </div>

            {postType === 'POST' && (
              <div
                ref={editorRef}
                className="post-content"
              />
            )}

            {postType === 'POLL' ? (
              <div className="poll-creation">
                <input
                  type="text"
                  placeholder="Ask a question..."
                  value={pollData.question}
                  onChange={(e) => {
                    setPollData(prev => ({ ...prev, question: e.target.value }));
                  }}
                  className={`poll-question ${validationError && !pollData.question.trim() ? 'error-field' : ''}`}
                  readOnly={false}
                  style={{}}
                />
                <div className="poll-options">
                  {pollData.options.map((option, index) => {
                    const isExisting = isEditing && index < existingPollOptionCount;
                    return (
                      <div key={index} className="add-poll-options">
                        <input
                          type="text"
                          placeholder={`Option ${index + 1}`}
                          value={option}
                          onChange={(e) => {
                            if (!isExisting) handlePollOptionChange(index, e.target.value);
                          }}
                          className={`${validationError && !option.trim() ? 'error-field' : ''}`}
                          readOnly={isExisting}
                          style={isExisting ? { background: '#f5f5f5', color: '#888', cursor: 'not-allowed' } : {}}
                        />
                        {!isExisting && pollData.options?.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removePollOption(index)}
                            className="remove-option"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {pollData.options?.length < 5 && (
                    <button
                      type="button"
                      onClick={addPollOption}
                      className="add-option"
                    >
                      <FaPlus /> Add Option
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="preview">
                  {url && <LinkPreview url={url} />}
                </div>

                {/* Unified media preview for both edit and create (images/videos) */}
                {(mediaFiles?.length > 0 || existingMedia?.length > 0) && (
                  <div className="media-preview">
                    {[...existingMedia, ...mediaFiles.map((media, index) => ({
                      id: `new-${index}`,
                      url: media.url,
                      type: media.file ? (media.file.type.startsWith('image/') ? 'image' : 'video') : 'image',
                      isExisting: false
                    }))].map((media, index) => (
                      <div key={media.id} className="media-preview-item">
                        {media.type === 'image' ? (
                          <img src={media.url} alt={`Media ${media.id}`} />
                        ) : (
                          <video src={media.url} controls />
                        )}
                        <button
                          type="button"
                          className="remove-media"
                          onClick={() => {
                            if (media.isExisting) {
                              removeExistingMedia(media.id);
                            } else {
                              removeMedia(index - existingMedia?.length);
                            }
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Show GIF preview in a separate view if selected */}
                {selectedGif && (
                  <div className="media-preview">
                    <div className="media-preview-item">
                      <img src={selectedGif.url} alt="Selected GIF" style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }} />
                      <button
                        type="button"
                        className="remove-media"
                        onClick={() => setSelectedGif(null)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Display validation error message below editors/poll sections */}
            {validationError && (
              <div className="validation-error-message">
                {validationError}
              </div>
            )}

            <div className="create-post-actions">
              {postType === 'POST' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(event) => {
                      const files = Array.from(event.target.files);
                      const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
                      if (invalidFiles?.length > 0) {
                        alert("Only image files are allowed.");
                      } else {
                        handleMediaSelect(event);
                      }
                    }}
                    id="media-upload"
                    className="media-input"
                  />
                  <label htmlFor="media-upload" className="media-upload-button" style={{ marginRight: 0 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <FaPaperclip style={{ verticalAlign: 'middle' }} />
                      {isEditing ? "More Photos" : "Photos"}
                    </span>
                  </label>
                  <button
                    type="button"
                    className="media-upload-button"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '"#f0f2f5"', border: 'none', cursor: selectedGif ? 'not-allowed' : 'pointer', fontSize: 16 }}
                    onClick={() => { if (!selectedGif) setShowGifPicker(true); }}
                    disabled={!!selectedGif}
                  >
                    GIF
                  </button>
                </div>
              )}
              <div className='buttons-container'>
                <button
                  type="button"
                  className="close-post"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-post"
                  disabled={isSubmitting || (postType === 'POLL' && (!pollData.question || pollData.options.some(opt => !opt)))}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    isEditing ? 'Save Changes' : (postType === 'POLL' ? 'Create Poll' : 'Post')
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* GIF Picker Modal */}
      {showGifPicker && (
        <div className="gif-picker-embedded" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 2000, background: 'rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowGifPicker(false)}>
          <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 10px 25px -5px rgba(106,87,55,0.2)', maxWidth: 420, maxHeight: 500, overflow: 'hidden', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #eee' }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>Select a GIF</span>
              <button style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }} onClick={() => setShowGifPicker(false)}>×</button>
            </div>
            <div style={{ padding: 8 }}>
              <GiphySearchBox
                apiKey="iRGwQCcO6qVRxtl6Svwg0KOHoHVX780w"
                onSelect={gif => {
                  setSelectedGif({ url: gif.images.fixed_height.url });
                  setShowGifPicker(false);
                }}
                searchPlaceholder="Search GIFs"
                poweredByGiphy={true}
                rating="g"
                autoFocus={true}
                masonryConfig={[
                  { columns: 3, imageWidth: 110, gutter: 8 },
                  { mq: "500px", columns: 3, imageWidth: 110, gutter: 8 },
                ]}
              />
            </div>
          </div>
        </div>
      )}      {/* Link Modal (for adding/editing links) */}
      <LinkModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onSave={handleLinkSave}
        initialUrl={linkModalData.url}
        initialText={linkModalData.text}
        isEdit={linkModalData.isEdit}
      />
    </div>
  );
};

export default AddPost;